// The 2D preview's copy and helpers: why the page draws it instead of the wasm viewport, and how to ask
// for it. Plain functions, so the tests read them without a DOM.
import type { NoViewport } from '../viewport/wgpu.ts';

/** Shown once a session: after Continue in 2D, a reload keeps the 2D preview without asking again. */
export const NOTICE_SEEN = 'ba.gpuNotice';

export const NOTICE_TITLE: Record<NoViewport['why'], string> = {
  'no-webgpu': 'This browser can’t draw the 3D brain',
  'no-adapter': 'This browser can’t draw the 3D brain',
  'no-engine': 'The 3D view isn’t on this server',
  failed: 'The 3D view didn’t start',
};

export function noticeWhy(f: NoViewport): string {
  switch (f.why) {
    case 'no-webgpu':
      return 'Its brains, vessels and cells are drawn with WebGPU, which this browser doesn’t have.';
    case 'no-adapter':
      return 'This browser has WebGPU, but found no graphics processor it can use. Turning on hardware acceleration in its settings may fix that.';
    case 'no-engine':
      return 'This copy of the app was published without its 3D viewport.';
    case 'failed':
      // One paragraph runs on after it, so the error ends as a sentence.
      return f.detail ? `It stopped with this error: ${f.detail.trim()}${/[.!?]$/.test(f.detail.trim()) ? '' : '.'}` : 'It stopped before it drew anything.';
  }
}

/** The HUD's caption over the 2D preview: two words of why, inside the caption's two-line clamp. */
export function previewNote(f: NoViewport): string {
  switch (f.why) {
    case 'no-webgpu':
      return '2D preview · no WebGPU';
    case 'no-adapter':
      return '2D preview · no usable GPU';
    case 'no-engine':
      return '2D preview · no 3D viewport';
    case 'failed':
      return '2D preview · 3D view failed';
  }
}

/** Why the page draws the 2D preview, for the Methods renderer row and the HUD caption's tooltip. */
export function previewWhy(f: NoViewport): string {
  switch (f.why) {
    case 'no-webgpu':
      return 'this browser has no WebGPU';
    case 'no-adapter':
      return 'no graphics processor this browser can use';
    case 'no-engine':
      return 'the 3D viewport isn’t on this server';
    case 'failed':
      return 'the 3D view didn’t start';
  }
}

/** The browsers that draw it by default, without flags (2026-10): Chrome and Edge from 113, Chrome on
 *  Android 12 and later, Safari 26, Firefox from 141 on Windows. */
export const BROWSERS = 'The 3D view draws in Chrome and Edge on Windows, macOS and ChromeOS, Chrome on recent Android phones, Safari 26 or later, and Firefox on Windows.';

/** How long the renderer may take to start before the loader offers the 2D preview: a stalled adapter or
 *  a slow download of the engine's 6 MB wasm. */
export const LATE_MS = 15_000;

/** The page as it is, drawn by the 2D preview. */
export function previewHref(href = location.href): string {
  const u = new URL(href);
  u.searchParams.set('engine', 'mock');
  return u.toString();
}
